import {
  Heart,
  Cross,
  Palette,
  Building2,
  Briefcase,
  PartyPopper,
  Theater,
  Drama,
  Music,
  Sparkles,
  Mountain,
  Wand2,
} from 'lucide-react';

export interface PortfolioImage {
  src: string;
  filename: string;
  alt: string;
}

export interface PortfolioCategory {
  slug: string;
  name: string;
  tagline: string;
  icon: any;
  images: PortfolioImage[];
}

// =============================================================================
// AUTO-DISCOVERY: scan toutes les images de src/assets via Vite glob
// =============================================================================
const assetModules = import.meta.glob<{ default: string }>(
  '/src/assets/**/*.{webp,jpg,jpeg,png}',
  { eager: true }
);

// Priorité d'extension : webp > jpg > jpeg > png (pour dédupliquer même nom de base)
const EXT_PRIORITY: Record<string, number> = { webp: 0, jpg: 1, jpeg: 2, png: 3 };
const getExt = (f: string) => f.toLowerCase().split('.').pop() || '';
const getBase = (f: string) => f.replace(/\.[^.]+$/, '').toLowerCase();

const rawAssets: { filename: string; src: string }[] = Object.entries(assetModules).map(
  ([path, mod]) => {
    const filename = path.split('/').pop() ?? '';
    return { filename, src: (mod as { default: string }).default };
  }
);

// Déduplication par nom de base : on garde l'extension prioritaire (webp d'abord)
const dedupedByBase = new Map<string, { filename: string; src: string }>();
for (const asset of rawAssets) {
  const base = getBase(asset.filename);
  const existing = dedupedByBase.get(base);
  if (
    !existing ||
    (EXT_PRIORITY[getExt(asset.filename)] ?? 99) <
      (EXT_PRIORITY[getExt(existing.filename)] ?? 99)
  ) {
    dedupedByBase.set(base, asset);
  }
}

const allAssets: { filename: string; src: string }[] = Array.from(dedupedByBase.values())
  // exclude logos, badges, blog covers, headers, hero, generic single visuals
  .filter(({ filename }) => {
    const f = filename.toLowerCase();
    // (exclusions ponctuelles)
    if (f.startsWith('logo')) return false;
    if (f.startsWith('badge')) return false;
    if (f.startsWith('blog-')) return false;
    if (f.startsWith('cat-')) return false;
    if (f.startsWith('hero-')) return false;
    if (f.startsWith('artistes-hero')) return false;
    if (f.startsWith('artistic-')) return false;
    if (f.startsWith('artist-')) return false;
    if (f.startsWith('cyrille-')) return false;
    if (f.startsWith('insta-')) return false;
    if (f.startsWith('portfolio-')) return false;
    if (f.startsWith('formula-')) return false;
    if (f.startsWith('event-award')) return false;
    if (f.includes('placeholder')) return false;
    return true;
  });

// Helper: prettify filename → alt text
const toAlt = (filename: string): string => {
  const base = filename.replace(/\.[^.]+$/, '').replace(/-min(-\d+)?$/, '');
  return base
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

// Match a filename against an array of prefix patterns
const matches = (filename: string, prefixes: string[]) => {
  const f = filename.toLowerCase();
  return prefixes.some((p) => f.startsWith(p.toLowerCase()));
};

const buildImages = (prefixes: string[], excludePrefixes: string[] = []): PortfolioImage[] => {
  const seenSrc = new Set<string>();
  return allAssets
    .filter(({ filename }) => matches(filename, prefixes))
    .filter(({ filename }) => !matches(filename, excludePrefixes))
    .filter(({ src }) => {
      if (seenSrc.has(src)) return false;
      seenSrc.add(src);
      return true;
    })
    .map(({ filename, src }) => ({ src, filename, alt: toAlt(filename) }))
    .sort((a, b) => a.filename.localeCompare(b.filename, undefined, { numeric: true }));
};

// Images extérieures à src/assets (public/) à intégrer manuellement
const flammeOlympiqueImages: PortfolioImage[] = [
  { src: '/images/flamme-olympique/carousel-1.webp', filename: 'flamme-olympique-1.webp', alt: 'Passage de la Flamme Olympique Paris 2024 — Cheerleaders' },
  { src: '/images/flamme-olympique/carousel-2.webp', filename: 'flamme-olympique-2.webp', alt: 'Passage de la Flamme Olympique Paris 2024 — Foule' },
  { src: '/images/flamme-olympique/carousel-3.webp', filename: 'flamme-olympique-3.webp', alt: 'Passage de la Flamme Olympique Paris 2024 — Animation' },
  { src: '/images/flamme-olympique/carousel-4.webp', filename: 'flamme-olympique-4.webp', alt: 'Passage de la Flamme Olympique Paris 2024 — Spectacle' },
  { src: '/images/flamme-olympique/carousel-5.webp', filename: 'flamme-olympique-5.webp', alt: 'Passage de la Flamme Olympique Paris 2024 — Bus officiel' },
];

// =============================================================================
// CATEGORIES — fusion existantes + nouvelles
// =============================================================================
export const portfolioCategories: PortfolioCategory[] = [
  {
    slug: 'mariage',
    name: 'Mariage',
    tagline: 'Des unions célébrées avec élégance et émotion.',
    icon: Heart,
    images: buildImages(['mariage-carousel', 'wedding-carousel', 'wedding-content']),
  },
  {
    slug: 'ceremonie-laique',
    name: 'Cérémonie Laïque',
    tagline: "L'art de dire oui autrement.",
    icon: Cross,
    images: buildImages(['ceremonie-carousel', 'officiant-carousel', 'ceremony-content']),
  },
  {
    slug: 'designer',
    name: 'Designer & Scénographie',
    tagline: 'La scénographie au service de vos événements.',
    icon: Palette,
    images: buildImages(['materiel-content', 'special-content', 'event-content']),
  },
  {
    slug: 'lieux-services-associes',
    name: 'Lieux & Services Associés',
    tagline: "Des lieux d'exception et des prestataires triés sur le volet.",
    icon: Building2,
    images: buildImages([
      'lieuxhub-carousel',
      'lieux-reception',
      'lieux-carousel',
      'lieux-content',
      'resto-carousel',
      'restauration-content',
      'beaute-carousel',
      'beaute-content',
      'transport-carousel',
      'transport-vtc',
      'transport-content',
      'location-carousel',
      'image-souvenirs',
      'image-carousel',
      'borne-selfie',
      'borne-content',
    ]),
  },
  {
    slug: 'corporate',
    name: 'Corporate & Institutionnel',
    tagline: 'Des événements professionnels à la hauteur de vos ambitions.',
    icon: Briefcase,
    images: buildImages(['sf-']), // savoir-faire = principalement événements pro/institutionnels
  },
  {
    slug: 'art-de-la-rue',
    name: 'Art de la rue',
    tagline: "L'énergie de la rue, l'audace du spectacle vivant.",
    icon: Theater,
    images: buildImages(['artrue-carousel', 'art-rue']),
  },
  {
    slug: 'art-de-la-scene',
    name: 'Art de la scène',
    tagline: 'La magie de la scène, sous toutes ses formes.',
    icon: Drama,
    images: buildImages(['artscene-carousel', 'art-scene', 'cirque-carousel', 'art-cirque', 'cirque-clown', 'cirque-contorsionniste']),
  },
  {
    slug: 'arts-visuels',
    name: 'Arts visuels',
    tagline: "L'art en direct, capté pour l'éternité.",
    icon: Wand2,
    images: buildImages(['artsvisuels-carousel', 'arts-visuels', 'av-']),
  },
  {
    slug: 'musique-dj',
    name: 'Musique & DJ',
    tagline: "Le rythme qui fait vibrer vos événements.",
    icon: Music,
    images: buildImages(['musique-carousel', 'animateur-carousel', 'music-content']),
  },
  {
    slug: 'animations-experiences',
    name: 'Animations & Expériences',
    tagline: 'Des expériences immersives qui marquent les esprits.',
    icon: Sparkles,
    images: [
      ...flammeOlympiqueImages,
      ...buildImages(['en-', 'animations-speciales', 'enfants-carousel', 'kids-content', 'fo-', 'feu-', 'flamme']),
    ],
  },
  {
    slug: 'sport-aventure',
    name: 'Sport & Aventure',
    tagline: 'Sensations fortes et performances spectaculaires.',
    icon: Mountain,
    images: buildImages(['sport-', 'aventure-carousel', 'aventure-content']),
  },
].filter((cat) => cat.images.length > 0);

// =============================================================================
// LEGACY EXPORTS (compat)
// =============================================================================
export interface PortfolioItem {
  id: number;
  title: string;
  location?: string;
  year?: string;
  gradient: string;
}

export const getAllImages = (): (PortfolioImage & { category: string; categoryName: string })[] => {
  const seen = new Set<string>();
  const all: (PortfolioImage & { category: string; categoryName: string })[] = [];
  portfolioCategories.forEach((cat) => {
    cat.images.forEach((img) => {
      if (seen.has(img.src)) return;
      seen.add(img.src);
      all.push({ ...img, category: cat.slug, categoryName: cat.name });
    });
  });
  return all;
};

// Kept for backward compat (some components may still import this)
export const getAllItems = getAllImages;
